import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Drop, Plus, Trash } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useConfirmDelete } from '@/components/ConfirmDialogs';
import { fmtDate as fmtDatePt } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;

export default function HidratacaoTab({ patientId }) {
  const confirmDelete = useConfirmDelete();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ date: new Date().toISOString().split('T')[0], amount_ml: '', notes: '' });

  const fetchRecords = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/patients/${patientId}/hydration`); setRecords(data || []); } catch { /* noop */ } finally { setLoading(false); }
  }, [patientId]);
  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const add = async () => {
    if (!form.amount_ml) return;
    try {
      await axios.post(`${API}/api/patients/${patientId}/hydration`, { date: form.date, amount_ml: parseInt(form.amount_ml, 10), notes: form.notes });
      toast.success('Hidratação guardada');
      setForm({ date: new Date().toISOString().split('T')[0], amount_ml: '', notes: '' });
      fetchRecords();
    } catch { toast.error('Erro ao guardar'); }
  };
  const del = async (id) => {
    const rec = records.find(r => r.id === id);
    const ok = await confirmDelete({ description: `Vai apagar o registo de hidratação de ${rec?.date || ''}.` });
    if (!ok) return;
    try { await axios.delete(`${API}/api/patients/${patientId}/hydration/${id}`); toast.success('Eliminado'); fetchRecords(); } catch { /* noop */ }
  };

  const chartData = records.slice(0, 14).reverse();
  const maxMl = Math.max(...chartData.map(r => r.amount_ml || 0), 1);

  return (
    <div className="space-y-4" data-testid="admin-hidratacao-tab">
      <div className="bg-white border border-brand-border rounded-2xl p-4">
        <h3 className="font-heading text-sm text-brand-text mb-3 flex items-center gap-2"><Drop size={16} /> Registar hidratação</h3>
        <div className="flex flex-wrap items-end gap-2">
          <div><Label className="text-xs text-brand-muted mb-1 block">Data</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="rounded-xl border-brand-border w-40" data-testid="admin-hydration-date" /></div>
          <div><Label className="text-xs text-brand-muted mb-1 block">Quantidade (ml)</Label><Input type="number" value={form.amount_ml} onChange={e => setForm({ ...form, amount_ml: e.target.value })} placeholder="Ex: 2000" className="rounded-xl border-brand-border w-32" data-testid="admin-hydration-amount" /></div>
          <div className="flex-1 min-w-[160px]"><Label className="text-xs text-brand-muted mb-1 block">Notas</Label><Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="rounded-xl border-brand-border text-sm" data-testid="admin-hydration-notes" /></div>
          <button onClick={add} className="bg-brand-green text-white rounded-full px-4 py-2 text-sm font-medium flex items-center gap-1" data-testid="admin-add-hydration-btn"><Plus size={14} /> Adicionar</button>
        </div>
      </div>

      {chartData.length >= 2 && (
        <div className="bg-white border border-brand-border rounded-2xl p-4">
          <h4 className="text-[10px] text-brand-muted uppercase tracking-wider mb-3">Evolução</h4>
          <div className="flex items-end justify-between gap-1 h-32">
            {chartData.map(r => (
              <div key={r.id} className="flex-1 flex flex-col items-center justify-end h-full" title={`${r.date}: ${r.amount_ml} ml`}>
                <div className="w-full rounded-t bg-brand-green" style={{ height: `${Math.max(((r.amount_ml || 0) / maxMl) * 100, 2)}%` }} />
                <span className="text-[8px] text-brand-muted mt-1">{r.date?.slice(5)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? <p className="text-center text-brand-muted py-6 text-sm">A carregar...</p> : records.length === 0 ? (
        <p className="text-center text-brand-muted py-8 text-sm">Sem registos de hidratação.</p>
      ) : (
        <div className="space-y-2">
          {records.map(r => (
            <div key={r.id} className="bg-white border border-brand-border rounded-xl p-3 flex items-center justify-between text-sm" data-testid={`admin-hydration-record-${r.id}`}>
              <div>
                <span className="text-xs text-brand-muted">{fmtDatePt(r.date)}</span>
                {r.notes && <p className="text-[10px] text-brand-muted italic">"{r.notes}"</p>}
              </div>
              <div className="flex items-center gap-3">
                <strong className="text-brand-text">{(r.amount_ml || 0).toLocaleString('pt-PT')} ml</strong>
                <button onClick={() => del(r.id)} className="text-brand-placeholder hover:text-red-500"><Trash size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
